import _ from 'lodash';
import {connect} from 'react-redux';

import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {CalendarVersionAccessRolesEnum} from '../../../../models/api/calendarVersionModel';
import {
  getFindCalendarVersionName,
  getVisibleCalendarVersions,
} from '../../../../store/data/calendarVersions/calendarVersionsSelectors';
import {getCalendars} from '../../../../store/data/dataSelectors';
import {selectCalendarVersionAction} from '../../../../store/panels/calendars/calendarsPanelActions';
import {
  makeGetSelectedCalendarVersionIds,
  makeGetViewingNowCalendars,
} from '../../../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarsPanelId} from '../../../../store/panels/panelStateBase';
import {MyCalendarsDropdown} from './myCalendarsDropdown';

/*
 * Props.
 */

interface MyCalendarsDropdownContainerProps extends DropdownProps {
  panelId: CalendarsPanelId;
}

/*
 * Selectors.
 */

function makeGetViewingNowCalendarVersionIds(
  getViewingNowCalendars: ReturnType<typeof makeGetViewingNowCalendars>,
) {
  return createSelector([getViewingNowCalendars], (viewingNowCalendars) =>
    _(viewingNowCalendars)
      .map((calendar) => calendar.calendarVersionId)
      .compact()
      .value(),
  );
}

function makeGetAvailableCalendarVersions(
  getSelectedCalendarVersionIds: ReturnType<typeof makeGetSelectedCalendarVersionIds>,
  getViewingNowCalendarVersionIds: ReturnType<typeof makeGetViewingNowCalendarVersionIds>,
) {
  return createSelector(
    [
      getCalendars,
      getVisibleCalendarVersions,
      getSelectedCalendarVersionIds,
      getViewingNowCalendarVersionIds,
    ],
    (calendars, visibleCalendarVersions, selectedCalendarVersionIds, viewingNowCalendarVersionIds) =>
      _(visibleCalendarVersions)
        // Remove selected calendar versions and viewing now calendars.
        .filter((calendarVersion) => !selectedCalendarVersionIds.includes(calendarVersion.id))
        .filter((calendarVersion) => !viewingNowCalendarVersionIds.includes(calendarVersion.id))
        .map((calendarVersion) => {
          const calendar = calendars[calendarVersion.calendarId];
          return calendar ? {calendar, calendarVersion} : null;
        })
        .compact()
        .sortBy(({calendar, calendarVersion}) => {
          const calendarDisplayName = (calendarVersion.nameOverride || calendar.name).toLowerCase();
          const isOwner = calendarVersion.accessRole === CalendarVersionAccessRolesEnum.OWNER;

          // Invert booleans as sortBy gives false values precedence
          return [!(isOwner && calendarVersion.isPrimary), !isOwner, calendarDisplayName];
        })
        .map(({calendarVersion}) => calendarVersion)
        .value(),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MyCalendarsDropdownContainerProps>()(() => {
  const getSelectedCalendarVersionIds = makeGetSelectedCalendarVersionIds();
  const getViewingNowCalendars = makeGetViewingNowCalendars();
  const getViewingNowCalendarVersionIds = makeGetViewingNowCalendarVersionIds(getViewingNowCalendars);

  return {
    calendarVersions: makeGetAvailableCalendarVersions(
      getSelectedCalendarVersionIds,
      getViewingNowCalendarVersionIds,
    ),
    findCalendarVersionName: getFindCalendarVersionName,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<MyCalendarsDropdownContainerProps>()(({panelId}) => ({
  onSelectCalendarVersion: (calendarVersionId) =>
    selectCalendarVersionAction(panelId, calendarVersionId, false),
}));

export const MyCalendarsDropdownContainer = connect(mapStateToProps, mapDispatchToProps)(MyCalendarsDropdown);
